import fnv1a from '../../math/prng/fnv1a';
import SB from '../../SchemaBuilder';
import type ServerAssetUploader from '../../server/ServerAssetUploader';
import type { TelegramClient } from '../../server/Telegram';
import { ResponseError } from '../../utils/ResponseError';
import { getContentTypeForImage } from '../../utils/Utils';
import type { KeyValueStoreAPI } from '../KeyValueStoreAPI';
import type { TelegramAPI } from '../TelegramAPI';
import type { Fetcher } from './utils/Fetcher';

const ASSET_PREFIX = 'tg-profile-';

export const inlinePurchasePayloadSchema = SB.object({
    key: SB.string().optional(), // Legacy payloads do not include `key`
    productId: SB.string(),
}).additionalProperties();

type InlinePurchasePayload = SB.ExtractType<typeof inlinePurchasePayloadSchema>;

export const remotePurchasePayloadSchema = SB.object({
    analytics: SB.object({
        feature: SB.string(),
        subFeature: SB.string(),
    }).additionalProperties(),
}).additionalProperties();

export type RemotePurchasePayload = SB.ExtractType<typeof remotePurchasePayloadSchema>;

export function createTelegramAPI(opts: {
    assetUploader: ServerAssetUploader;
    fetcher: Fetcher;
    kvStore: KeyValueStoreAPI;
    telegramClient: TelegramClient;
    userAssetsBaseUrl: string;
}): TelegramAPI {
    const { assetUploader, fetcher, kvStore, telegramClient, userAssetsBaseUrl } = opts;

    async function getProfilePictureOfSize(
        userId: string | number,
        size: '160x160' | '320x320' | '640x640',
    ): Promise<{ fileId: string; fileUniqueId: string } | void> {
        const allPictures = await telegramClient.getUserProfilePhotos({ userId });

        const sizeMapping = {
            '160x160': [160, 160],
            '320x320': [320, 320],
            '640x640': [640, 640],
        };

        const [width, height] = sizeMapping[size];

        if (!width || !height) {
            throw Error(`Invalid size argument : ${size}`);
        }

        return (allPictures || []).find((pic) => pic.width === width && pic.height === height);
    }

    async function getCachedAssetUrl(opts: { fileUniqueId: string; fileId: string }) {
        const assetId = ASSET_PREFIX + opts.fileUniqueId;
        const assetUrl = userAssetsBaseUrl + assetId;

        // If we've already uploaded the file to user assets, return the asset URL:
        if (await assetUploader.assetExists(assetId)) {
            return assetUrl;
        }

        // If asset does not exist, fetch the picture using Telegram's temporary download link and upload to user assets:
        const { fileUrl } = await telegramClient.getFile(opts.fileId);

        const fileResponse = await fetcher.fetch({
            binary: true,
            method: 'GET',
            url: fileUrl,
            timeout: 6_000,
            headers: {},
        });

        if (fileResponse.statusCode < 200 || fileResponse.statusCode >= 300) {
            throw await ResponseError.fromResponse('Unexpected Telegram file API response', fileResponse);
        }

        const body = Buffer.from(fileResponse.body, 'binary');
        const contentType = getContentTypeForImage(fileUrl);

        await assetUploader.uploadAsset({ assetId, body, contentType });

        return assetUrl;
    }

    return {
        async getProfilePictureUrl(userId, size) {
            const picture = await getProfilePictureOfSize(userId, size);
            if (!picture) {
                return;
            }
            return getCachedAssetUrl(picture);
        },

        async getChatPictureUrl(chatId, size) {
            const chat = await telegramClient.getChat({ chatId });
            if (!chat) {
                return;
            }

            const { photo } = chat;
            if (!photo) {
                return; // No photo
            }

            const file =
                size === '160x160'
                    ? { fileUniqueId: photo.smallFileUniqueId, fileId: photo.smallFileId }
                    : { fileUniqueId: photo.bigFileUniqueId, fileId: photo.bigFileId };
            return getCachedAssetUrl(file);
        },

        async createInvoiceLink(opts) {
            // Work around the 128-byte payload size limit by storing the payload in KV store:
            const remotePayload: RemotePurchasePayload = { analytics: opts.analytics };
            remotePurchasePayloadSchema.tryValidate(remotePayload);
            const kvStoreValue = JSON.stringify(remotePayload);

            const inlinePayload: InlinePurchasePayload = {
                key: `kv-tgIapPayload-${fnv1a(kvStoreValue)}`,
                productId: opts.productId, // Include product ID in KV store key so that we can fall back on it if KV store lookup fails
            };
            const kvStoreKey = JSON.stringify(inlinePayload);

            await kvStore.send(kvStoreKey, kvStoreValue);

            const { invoiceLink } = await telegramClient.createInvoiceLink({
                title: opts.title,
                description: opts.description,
                priceInStars: opts.priceInStars,
                payload: kvStoreKey,
                imageUrl: opts.imageUrl,
            });
            return invoiceLink;
        },

        async isSubscribedToChannel(opts) {
            const member = await telegramClient.getChatMember({ userId: opts.userId, chatId: `@${opts.channelId}` });
            return !!member && member.status !== 'left' && member.status !== 'kicked';
        },
    };
}

export function createStubTelegramAPI(errorMessage?: string): TelegramAPI {
    const throwError = () => {
        throw Error(errorMessage ?? 'Configure the TELEGRAM_BOT_TOKEN environment variable to use `api.telegram.*`');
    };

    return {
        getProfilePictureUrl: throwError,
        getChatPictureUrl: throwError,
        createInvoiceLink: throwError,
        isSubscribedToChannel: throwError,
    };
}
